import React, { useState, useEffect, useCallback } from 'react';
import { TextWidget } from './widgets/TextWidget';
import { NumberWidget } from './widgets/NumberWidget';
import { TextareaWidget } from './widgets/TextareaWidget';
import { SliderWidget } from './widgets/SliderWidget';
import { SelectWidget } from './widgets/SelectWidget';
import { SeedWidget } from './widgets/SeedWidget';
import { SwitchWidget } from './widgets/SwitchWidget';
import { FileUploadWidget } from './widgets/FileUploadWidget';
import { MultiImageUploadWidget } from './widgets/MultiImageUploadWidget';
import { ResolutionWidget } from './widgets/ResolutionWidget';
import { AspectRatioWidget } from './widgets/AspectRatioWidget';
import { RichDropdownWidget, RichDropdownItem } from './widgets/RichDropdownWidget';
import { useTenant } from '../../contexts/TenantContext';
import { useStudioContext, Product } from '../../contexts/StudioContext';
import { assetService } from '../../services/assetService';


// Reserved keys that are automatically injected from the active product context.
// If a workflow field matches one of these names and a product is selected, the
// value is pre-filled and the required-field validation is bypassed.
const RESERVED_CONTEXT_KEYS: Record<string, (p: Product) => any> = {
  product_id:             (p) => p.productId,
  product_name:           (p) => p.name,
  product_description:    (p) => p.description,
  product_primary_image:  (p) => p.primaryImage,
  product_sku:            (p) => p.sku,
  product_variant:        (p) => p.variant,
  product_classification: (p) => p.classification,
  product_source_url:     (p) => p.sourceUrl,
};

/** Returns a { key: value } map of reserved context values for a given product. */
function getProductContextValues(product: Product): Record<string, any> {
  const values: Record<string, any> = {};
  for (const [key, getter] of Object.entries(RESERVED_CONTEXT_KEYS)) {
    const v = getter(product);
    if (v !== undefined && v !== null && v !== '') {
      values[key] = v;
    }
  }
  return values;
}

interface FormRendererProps {
  schema: any;
  uiSchema?: any;
  onSubmit: (inputs: Record<string, any>) => void;
  disabled?: boolean;
}

export const FormRenderer: React.FC<FormRendererProps> = ({
  schema,
  uiSchema,
  onSubmit,
  disabled = false
}) => {
  const { currentTenant } = useTenant();
  const { context } = useStudioContext();
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [resolvedAssetUrls, setResolvedAssetUrls] = useState<Record<string, string>>({});

  // Convert JSONForms UI schema format to simple format
  const normalizedUiSchema = React.useMemo(() => {
    if (!uiSchema) return {};

    // If it's already in simple format (has field names as keys), return as-is
    if (!uiSchema.elements && !uiSchema.type) {
      return uiSchema;
    }

    // Convert JSONForms format to simple format
    const normalized: Record<string, any> = {};
    if (uiSchema.elements && Array.isArray(uiSchema.elements)) {
      const extractFields = (elements: any[]) => {
        for (const element of elements) {
          if (element.type === 'Control' && element.scope) {
            // Extract field name from scope: "#/properties/field_name" -> "field_name"
            const fieldName = element.scope.replace('#/properties/', '');
            normalized[fieldName] = {
              'ui:widget': element.options?.widget || undefined,
              'ui:label': element.options?.label || undefined,
              'ui:helpText': element.options?.helpText || undefined,
              'ui:customEnumOptions': element.options?.customEnumOptions || undefined,
              'ui:resolutionPair': element.options?.resolutionPair || undefined,
              'ui:richDropdownItems': element.options?.richDropdownItems || undefined,
              'ui:placeholder': element.options?.placeholder || undefined,
              'ui:displayMode': element.options?.displayMode || undefined,
              'ui:source': element.options?.source || undefined,
              'ui:contextId': element.options?.contextId || undefined,
              'ui:allowMultiple': element.options?.allowMultiple || undefined,
              'ui:hidden': element.options?.hidden ? true : undefined,
            };
          } else if (element.type === 'Group' && element.elements) {
            // Recursively process group elements
            extractFields(element.elements);
          } else if (element.type === 'VerticalLayout' && element.elements) {
            // Recursively process layout elements
            extractFields(element.elements);
          }
        }
      };
      extractFields(uiSchema.elements);
    }
    return normalized;
  }, [uiSchema]);

  // Resolve asset:// URLs to actual blob URLs
  const resolveAssetUrl = useCallback(async (url: string): Promise<string> => {
    if (!url || !url.startsWith('asset://')) return url;
    if (resolvedAssetUrls[url]) return resolvedAssetUrls[url];

    const tenantId = currentTenant?.tenantId || '';
    if (!tenantId) return url;

    try {
      const resolved = await assetService.resolveAssetUrl(url, tenantId);
      setResolvedAssetUrls(prev => ({ ...prev, [url]: resolved }));
      return resolved;
    } catch (err) {
      console.warn('Failed to resolve asset URL:', url, err);
      return url;
    }
  }, [currentTenant?.tenantId, resolvedAssetUrls]);

  // Pre-resolve all asset:// URLs in rich dropdown items
  useEffect(() => {
    const resolveAllAssets = async () => {
      const assetUrls: string[] = [];

      // Collect all asset:// URLs from normalized UI schema
      Object.values(normalizedUiSchema).forEach((config: any) => {
        const items = config?.['ui:richDropdownItems'] || [];
        items.forEach((item: RichDropdownItem) => {
          if (item.thumbnail?.startsWith('asset://')) {
            assetUrls.push(item.thumbnail);
          }
        });
      });

      // Resolve all unique URLs
      const uniqueUrls = Array.from(new Set(assetUrls));
      for (const url of uniqueUrls) {
        await resolveAssetUrl(url);
      }
    };

    if (currentTenant?.tenantId) {
      resolveAllAssets();
    }
  }, [normalizedUiSchema, currentTenant?.tenantId]);

  // Initialize form data with defaults ONLY when schema changes (workflow selection changes)
  // Use a stable identifier (JSON stringified schema) to prevent unnecessary resets
  const schemaKey = React.useMemo(() => JSON.stringify(schema), [schema]);

  useEffect(() => {
    console.log('🔄 FormRenderer: Schema changed, resetting form to defaults');
    const defaults: Record<string, any> = {};
    if (schema?.properties) {
      Object.keys(schema.properties).forEach(key => {
        const prop = schema.properties[key];
        if (prop.default !== undefined) {
          defaults[key] = prop.default;
        }
      });
    }
    // Pre-fill context-sourced product-list fields
    Object.entries(normalizedUiSchema).forEach(([fieldName, config]: [string, any]) => {
      if (config?.['ui:source'] === 'context' && config?.['ui:widget'] === 'product-list') {
        if (context.selectedProducts.length > 0) {
          defaults[fieldName] = JSON.stringify(context.selectedProducts);
        }
      }
      // Pre-fill context-product fields with the active product's source URL
      if (config?.['ui:widget'] === 'context-product') {
        if (context.selectedProducts.length > 0) {
          defaults[fieldName] = context.selectedProducts[0].sourceUrl || '';
        }
      }
    });
    // Pre-fill reserved context key fields from the selected product
    if (context.selectedProducts.length > 0) {
      const contextValues = getProductContextValues(context.selectedProducts[0]);
      if (schema?.properties) {
        Object.keys(schema.properties).forEach(key => {
          if (key in contextValues) {
            defaults[key] = contextValues[key];
          }
        });
      }
    }
    console.log('📝 FormRenderer: Setting form defaults:', defaults);
    setFormData(defaults);
    setIsInitialized(true);
  }, [schemaKey]); // Use schemaKey instead of schema to prevent unnecessary resets

  // Sync context-sourced fields whenever selected products change
  useEffect(() => {
    if (!isInitialized) return;
    setFormData(prev => {
      const next = { ...prev };
      let changed = false;

      // Sync product-list widgets and context-product widgets
      Object.entries(normalizedUiSchema).forEach(([fieldName, config]: [string, any]) => {
        if (config?.['ui:source'] === 'context' && config?.['ui:widget'] === 'product-list') {
          const newValue = context.selectedProducts.length > 0
            ? JSON.stringify(context.selectedProducts)
            : '';
          if (next[fieldName] !== newValue) {
            next[fieldName] = newValue;
            changed = true;
          }
        }
        // Sync context-product fields: track active product's source URL
        if (config?.['ui:widget'] === 'context-product') {
          const newValue = context.selectedProducts.length > 0
            ? context.selectedProducts[0].sourceUrl || ''
            : '';
          if (next[fieldName] !== newValue) {
            next[fieldName] = newValue;
            changed = true;
          }
        }
      });

      // Sync reserved context key fields
      if (schema?.properties) {
        const contextValues = context.selectedProducts.length > 0
          ? getProductContextValues(context.selectedProducts[0])
          : {};
        Object.keys(schema.properties).forEach(key => {
          if (key in RESERVED_CONTEXT_KEYS) {
            const newValue = contextValues[key] ?? '';
            if (next[key] !== newValue) {
              next[key] = newValue;
              changed = true;
            }
          }
        });
      }

      return changed ? next : prev;
    });
  }, [context.selectedProducts, normalizedUiSchema, isInitialized, schema]);

  const handleFieldChange = (field: string, value: any) => {
    console.log(`✏️  FormRenderer: Field "${field}" changed to:`, value);
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    // Compute which reserved context keys are currently satisfied by the active product
    const satisfiedByContext = context.selectedProducts.length > 0
      ? new Set(Object.keys(getProductContextValues(context.selectedProducts[0])))
      : new Set<string>();

    if (schema?.required) {
      schema.required.forEach((field: string) => {
        // Skip validation for fields that will be auto-filled from active context
        if (satisfiedByContext.has(field)) return;
        // Skip validation for hidden fields — they always submit with their default value
        if (normalizedUiSchema[field]?.['ui:hidden']) return;
        const value = formData[field];
        const isMissing = Array.isArray(value)
          ? value.length === 0
          : !value && value !== 0 && value !== false;
        if (isMissing) {
          // Give a helpful message for context-product fields that have no product selected
          if (normalizedUiSchema[field]?.['ui:widget'] === 'context-product') {
            errors[field] = 'Select a product in the Context Panel to use this input';
          } else {
            errors[field] = 'This field is required';
          }
        }
      });
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted with data:', formData);
    if (!validateForm()) {
      console.log('Form validation failed:', validationErrors);
      return;
    }
    console.log('Form validation passed, calling onSubmit');
    setSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleGroup = (groupLabel: string) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupLabel]: !prev[groupLabel]
    }));
  };

  const renderField = (fieldName: string, fieldSchema: any) => {
    const uiConfig = normalizedUiSchema?.[fieldName] || {};
    let widgetType = uiConfig['ui:widget'] || fieldSchema.type;

    // Context-sourced fields (product-list, sheet-row, etc.) are owned by the
    // left sidebar (ContextPanel). Their values are injected into the job
    // payload at submit time by WorkflowStudio, so the right-hand form must
    // not render them — otherwise the user sees a stray text field for a
    // selection they're meant to make in the sidebar.
    if (uiConfig['ui:source'] === 'context') {
      return null;
    }

    // If field has enum values but no explicit widget, use select
    if ((fieldSchema.enum || uiConfig['ui:customEnumOptions']) && !uiConfig['ui:widget']) {
      widgetType = 'select';
    }

    // Map legacy "image-upload" to "image" widget
    if (widgetType === 'image-upload') {
      widgetType = 'image';
    }

    if (fieldSchema.type === 'array' && (widgetType === 'image' || widgetType === 'file')) {
      widgetType = 'images';
    }

    // Explicit context-product widget: declared by the workflow author via param_type=Context_Product
    if (widgetType === 'context-product') {
      const label = uiConfig['ui:label'] || fieldSchema.title || fieldName;
      const hasProduct = context.selectedProducts.length > 0;
      const product = context.selectedProducts[0];
      const errorMsg = validationErrors[fieldName];
      return (
        <div key={fieldName} className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">{label}</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
              From Context
            </span>
          </div>
          {hasProduct ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded text-sm">
              {product.primaryImage && (
                <img src={product.primaryImage} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
              )}
              <div className="min-w-0">
                <div className="font-medium text-gray-800 truncate">{product.name}</div>
                <div className="text-gray-500 text-xs truncate" title={product.sourceUrl}>{product.sourceUrl}</div>
              </div>
            </div>
          ) : (
            <div className={`px-3 py-2 rounded-md text-sm italic border border-dashed ${errorMsg ? 'bg-red-50 border-red-300 text-red-600' : 'bg-gray-50 border-gray-300 text-gray-500'}`}>
              No product selected — choose one in the Context Panel
            </div>
          )}
          {errorMsg && <p className="text-xs text-red-600">{errorMsg}</p>}
        </div>
      );
    }

    // Check if this field is being auto-filled from the active product context
    const isFromContext = fieldName in RESERVED_CONTEXT_KEYS &&
      context.selectedProducts.length > 0 &&
      !!getProductContextValues(context.selectedProducts[0])[fieldName];

    // If the field is fully satisfied by context, render a compact read-only badge instead of an editable widget
    if (isFromContext) {
      const label = uiConfig['ui:label'] || fieldSchema.title || fieldName;
      const contextValue = formData[fieldName] ?? '';
      return (
        <div key={fieldName} className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">{label}</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
              From Context
            </span>
          </div>
          <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded px-3 py-1.5 truncate" title={String(contextValue)}>
            {String(contextValue)}
          </div>
        </div>
      );
    }

    const value = formData[fieldName];
    const error = validationErrors[fieldName];

    // Use custom enum options if available, otherwise fall back to schema enum
    const enumOptions = uiConfig['ui:customEnumOptions'] || fieldSchema.enum;
    
    const commonProps = {
      value,
      onChange: (newValue: any) => handleFieldChange(fieldName, newValue),
      disabled: submitting || disabled,
      error,
      label: uiConfig['ui:label'] || fieldSchema.title || fieldName,
      description: fieldSchema.description,
      helpText: uiConfig['ui:helpText'] || undefined
    };

    switch (widgetType) {
      case 'resolution-width': {
        // Resolution widget controls both width and height fields
        const heightFieldName = uiConfig['ui:resolutionPair'] || normalizedUiSchema?.[fieldName]?.['ui:resolutionPair'];
        if (!heightFieldName) {
          console.warn(`Resolution width field ${fieldName} missing resolutionPair config`);
          return <NumberWidget key={fieldName} {...commonProps} />;
        }
        
        const heightValue = formData[heightFieldName];
        const heightSchema = schema.properties?.[heightFieldName];
        
        return (
          <ResolutionWidget
            key={fieldName}
            label={commonProps.label}
            description={commonProps.description}
            helpText={commonProps.helpText}
            widthValue={value}
            heightValue={heightValue}
            onWidthChange={(newWidth) => handleFieldChange(fieldName, newWidth)}
            onHeightChange={(newHeight) => handleFieldChange(heightFieldName, newHeight)}
            disabled={commonProps.disabled}
            error={error}
          />
        );
      }
      case 'textarea':
        return <TextareaWidget key={fieldName} {...commonProps} />;
      case 'slider':
        return <SliderWidget key={fieldName} {...commonProps} min={fieldSchema.minimum} max={fieldSchema.maximum} step={fieldSchema.multipleOf || 1} />;
      case 'aspect-ratio':
        return <AspectRatioWidget key={fieldName} {...commonProps} />;
      case 'rich-dropdown': {
        // Rich dropdown with thumbnails, titles, and subtitles
        const richItems: RichDropdownItem[] = uiConfig['ui:richDropdownItems'] ||
          fieldSchema.richDropdownItems ||
          [];
        // Resolve asset:// URLs to actual blob URLs
        const resolvedItems = richItems.map(item => ({
          ...item,
          thumbnail: item.thumbnail && resolvedAssetUrls[item.thumbnail]
            ? resolvedAssetUrls[item.thumbnail]
            : item.thumbnail
        }));
        const displayMode = uiConfig['ui:displayMode'] || 'dropdown';
        return (
          <RichDropdownWidget
            key={fieldName}
            {...commonProps}
            items={resolvedItems}
            placeholder={uiConfig['ui:placeholder'] || 'Select an option...'}
            displayMode={displayMode}
          />
        );
      }
      case 'sheet-row': {
        // Context-sourced sheet row selection — read-only, driven by ContextPanel.
        // The actual tab/row are injected at submit time as `${contextId}__tab`
        // and `${contextId}__row`; the form just echoes the current selection.
        const contextId = uiConfig['ui:contextId'] || fieldName;
        const sel = context.sheetSelections[contextId];
        const label = uiConfig['ui:label'] || fieldSchema.title || fieldName;
        return (
          <div key={fieldName} className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">{label}</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                From Context
              </span>
            </div>
            {sel ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-md text-sm text-gray-800">
                {sel.row?.Thumbnail && (
                  <img
                    src={sel.row.Thumbnail}
                    alt={sel.rowTitle}
                    className="w-8 h-8 object-cover rounded flex-shrink-0"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
                <div className="min-w-0">
                  <div className="font-medium truncate">{sel.rowTitle}</div>
                  <div className="text-gray-500 text-xs truncate">{sel.tab}</div>
                </div>
              </div>
            ) : (
              <div className="px-3 py-2 bg-gray-50 border border-dashed border-gray-300 rounded-md text-sm text-gray-500 italic">
                No selection — choose one in the Context Panel
              </div>
            )}
          </div>
        );
      }
      case 'product-list': {
        // Context-sourced product selection — read-only, driven by ContextPanel
        const selectedProducts: Product[] = context.selectedProducts;
        const label = uiConfig['ui:label'] || fieldSchema.title || fieldName;
        const hasProducts = selectedProducts.length > 0;
        return (
          <div key={fieldName} className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">{label}</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                From Context
              </span>
            </div>
            {hasProducts ? (
              <div className="space-y-1">
                {selectedProducts.map((p: Product) => (
                  <div
                    key={p.productId}
                    className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-md text-sm text-gray-800"
                  >
                    {p.primaryImage && (
                      <img
                        src={p.primaryImage}
                        alt={p.name}
                        className="w-8 h-8 object-cover rounded flex-shrink-0"
                      />
                    )}
                    <span className="font-medium">{p.name}</span>
                    {p.variant && <span className="text-gray-500">· {p.variant}</span>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-3 py-2 bg-gray-50 border border-dashed border-gray-300 rounded-md text-sm text-gray-500 italic">
                No product selected — choose one in the Context Panel
              </div>
            )}
          </div>
        );
      }
      case 'select':
        // Convert enum strings to SelectOption format
        const selectOptions = (enumOptions || []).map((opt: any) => {
          if (typeof opt === 'string') {
            return { value: opt, label: opt };
          }
          return opt; // Already in correct format
        });
        return <SelectWidget key={fieldName} {...commonProps} options={selectOptions} />;
      case 'seed':
        return <SeedWidget key={fieldName} {...commonProps} />;
      case 'switch':
      case 'boolean':
        return <SwitchWidget key={fieldName} {...commonProps} />;
      case 'image':
      case 'file':
        return <FileUploadWidget key={fieldName} {...commonProps} accept="image/*" />;
      case 'images':
      case 'multi-image':
      case 'image-list':
        return <MultiImageUploadWidget key={fieldName} {...commonProps} maxFiles={fieldSchema.maxItems || 12} />;
      case 'video':
        return <FileUploadWidget key={fieldName} {...commonProps} accept="video/*" maxSizeMB={100} />;
      case 'document':
        return <FileUploadWidget key={fieldName} {...commonProps} accept="*/*" />;
      case 'number':
      case 'integer':
        return <NumberWidget key={fieldName} {...commonProps} min={fieldSchema.minimum} max={fieldSchema.maximum} step={fieldSchema.multipleOf || (fieldSchema.type === 'integer' ? 1 : 0.01)} />;
      case 'string':
      default:
        return <TextWidget key={fieldName} {...commonProps} />;
    }
  };

  const renderElement = (element: any, index: number): React.ReactNode => {
    // Handle Control elements (individual fields)
    if (element.type === 'Control' && element.scope) {
      const fieldName = element.scope.replace('#/properties/', '');
      const fieldSchema = schema.properties?.[fieldName];
      if (!fieldSchema) return null;
      // Hidden fields are not rendered, but their default value is still in formData
      // and is submitted at execution time.
      if (element.options?.hidden) return null;
      return renderField(fieldName, fieldSchema);
    }

    // Handle Group elements (collapsible sections)
    if (element.type === 'Group') {
      const groupLabel = element.label || `Group ${index}`;
      const isCollapsible = element.options?.collapsible ?? false;
      const isCollapsed = collapsedGroups[groupLabel] ?? (element.options?.collapsed ?? false);

      return (
        <div key={`group-${index}`} className="border border-gray-200 rounded-lg overflow-hidden">
          <div
            className={`flex items-center justify-between px-4 py-3 bg-surface-subtle border-b border-gray-200 ${isCollapsible ? 'cursor-pointer hover:bg-gray-100 transition-colors duration-fast' : ''}`}
            onClick={() => isCollapsible && toggleGroup(groupLabel)}
          >
            <h3 className="text-label font-semibold text-gray-700 uppercase tracking-wider">
              {groupLabel}
            </h3>
            {isCollapsible && (
              <span className={`text-gray-400 text-xs transition-transform duration-base ${isCollapsed ? '' : 'rotate-180'}`}>▼</span>
            )}
          </div>
          {!isCollapsed && element.elements && (
            <div className="p-4 flex flex-col gap-5">
              {element.elements.map((childElement: any, childIndex: number) =>
                renderElement(childElement, childIndex)
              )}
            </div>
          )}
        </div>
      );
    }

    // Handle VerticalLayout (just render children)
    if (element.type === 'VerticalLayout' && element.elements) {
      return (
        <React.Fragment key={`layout-${index}`}>
          {element.elements.map((childElement: any, childIndex: number) =>
            renderElement(childElement, childIndex)
          )}
        </React.Fragment>
      );
    }

    return null;
  };

  if (!schema || !schema.properties) {
    return <div className="p-4 text-center text-gray-500">No form schema available</div>;
  }

  // Determine rendering mode: use UI schema if available, otherwise fallback to schema properties
  const useUiSchemaRendering = uiSchema && uiSchema.elements && Array.isArray(uiSchema.elements);

  return (
    <form onSubmit={handleSubmit} className="space-y-6" id="workflow-form">
      {useUiSchemaRendering ? (
        // Render using UI schema structure (supports groups, layouts, etc.)
        uiSchema.elements.map((element: any, index: number) => renderElement(element, index))
      ) : (
        // Fallback: render all fields directly from schema
        Object.keys(schema.properties).map(fieldName => renderField(fieldName, schema.properties[fieldName]))
      )}
    </form>
  );
};

export default FormRenderer;
